import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase.generated';
import geohash from 'ngeohash';

// Configuration Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'placeholder-key';

// Debug en developpement
if (process.env.NODE_ENV === 'development') {
  console.log('🔍 DEBUG Supabase:', {
    url: process.env.REACT_APP_SUPABASE_URL ? 'PRESENT' : 'MISSING',
    key: process.env.REACT_APP_SUPABASE_ANON_KEY ? 'PRESENT' : 'MISSING'
  });
}

if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
  console.warn('⚠️ Variables Supabase manquantes, mode placeholder active');
}

// Client Supabase type avec le schema Database
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    // On gère manuellement les tokens OAuth pour éviter les blocages
    detectSessionInUrl: false,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  // Options globales
  global: {
    headers: {
      'x-client-info': 'event-connect-web'
    }
  }
});

// =============================================
// Types re-exportes pour commodite
// =============================================

// Types générés automatiquement par Supabase CLI
export type { Database } from '../types/supabase.generated';
export type { Tables, TablesInsert, TablesUpdate } from '../types/supabase.generated';

// Types métier avec helpers de conversion (camelCase <-> snake_case)
export type {
  DbUser,
  DbOrganizer,
  DbOrganizerInsert,
  DbOrganizerUpdate,
  DbEvent,
  DbEventSeries,
  DbUserFavorite,
  DbUserHiddenEvent,
  DbAnalyticsEvent,
  DbAnalyticsEventInsert,
  DbOrganizerZone,
  DbOrganizerZoneInsert,
  PriceItem,
  OrganizerStats,
  EventWithDistance,
  EventWithOrganizer,
  // Stripe RPC response types (migration 017)
  CheckCanPublishResponse,
  PublishEventResponse,
} from '../types/supabase.types';

// Helpers de conversion
export {
  dbEventToEvent,
  eventToDbEvent,
  dbOrganizerToProfile,
  profileToDbOrganizer,
  dbSeriesToEventSeries,
} from '../types/supabase.types';

// =============================================
// Types pour les analytics (existants)
// =============================================
export interface AnalyticsEvent {
  id?: string;
  event_name: string;
  geohash: string;
  organizer_id?: string;
  event_id?: string;
  created_at?: string;
  week_key: string;
  day_key: string;
  metadata?: Record<string, unknown>;
}

export interface OrganizerZone {
  organizer_id: string;
  geohash: string;
  latitude: number;
  longitude: number;
  address?: string | null;
  updated_at?: string | null;
}

// =============================================
// Fonctions utilitaires
// =============================================

/**
 * Genere les cles temporelles pour les analytics
 */
export const generateTimeKeys = (date: Date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  // ISO week number
  const firstDayOfYear = new Date(year, 0, 1);
  const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
  const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);

  return {
    day_key: `${year}-${month}-${day}`,
    week_key: `${year}-W${String(weekNumber).padStart(2, '0')}`
  };
};

// Precision geohash standard pour l'app
const GEOHASH_PRECISION = 5; // ~4.9km x 4.9km (couverture ~15km avec 9 zones)

/**
 * Genere un geohash depuis des coordonnees
 * Remplace l'ancien generateZoneKey
 */
export const generateGeohash = (lat: number, lng: number): string => {
  return geohash.encode(lat, lng, GEOHASH_PRECISION);
};

/**
 * @deprecated Utiliser generateGeohash a la place
 * Alias pour compatibilite retro-active
 */
export const generateZoneKey = (lat: number, lng: number, _radius: number = 10): string => {
  return generateGeohash(lat, lng);
};

// 🔧 DEBUG: Exposer le client Supabase dans la console pour le debugging
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).__supabase = supabase;
  console.log('🔧 DEBUG: Client Supabase accessible via window.__supabase');
}

/**
 * Verifie si Supabase est correctement configure
 */
export const isSupabaseConfigured = (): boolean => {
  return Boolean(
    process.env.REACT_APP_SUPABASE_URL &&
    process.env.REACT_APP_SUPABASE_ANON_KEY &&
    !process.env.REACT_APP_SUPABASE_URL.includes('placeholder')
  );
};
